import React, { createContext, useEffect, useState, useCallback, useMemo } from 'react';
import { User, Session, AuthResponse } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';
import { performEmergencyCleanup, isStorageNearQuota } from '../utils/localStorageCleanup';
import { createLogger } from '../utils/logger';
import { identifyUser as rcIdentifyUser, logoutRevenueCat } from '../services/revenuecat';
import { isNativeApp } from '../utils/platform';
import { trackEmailSignUp, trackOAuthSignUpIfNew } from '../utils/analytics';

const NATIVE_REDIRECT_URI = 'com.wallstreetle.app://auth/callback';

// Lazy-import @capacitor/browser so web builds never pull in the native plugin.
// We split loading (async, returns void) from access (sync) because Capacitor
// plugin proxies have a .then() method — returning one from an async function
// causes `await` to treat it as a thenable and call .then(), which throws.
let _Browser: typeof import('@capacitor/browser').Browser | null = null;
async function loadBrowser(): Promise<void> {
  if (!_Browser) {
    const mod = await import('@capacitor/browser');
    _Browser = mod.Browser;
  }
}

export interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, username?: string, captchaToken?: string) => Promise<AuthResponse>;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<AuthResponse>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  updateUsername: (newUsername: string) => Promise<{ error: any }>;
  resendVerificationEmail: () => Promise<{ error: any }>;
  isEmailVerified: boolean;
  checkUsernameAvailability: (username: string) => Promise<boolean>;
  checkEmailAvailability: (email: string) => Promise<boolean>;
  checkEmailConfirmationStatus: (email: string) => Promise<{
    exists: boolean;
    confirmed: boolean;
    created_at: string | null;
    available: boolean;
  }>;
  acceptTerms: (termsVersionId: string) => Promise<{ error: any }>;
  requestPasswordReset: (email: string) => Promise<{ error: any }>;
  resetPassword: (newPassword: string) => Promise<{ error: any }>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export interface AuthProviderProps {
  children: React.ReactNode;
}

const authLogger = createLogger('AuthContext');

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Existing native sessions do not emit SIGNED_IN on app launch. RevenueCat
      // still needs the Supabase UUID before any purchase/restore can be linked.
      if (session?.user?.id) {
        rcIdentifyUser(session.user.id).catch(() => {});
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      // Log critical token refresh failures
      if (_event === 'TOKEN_REFRESHED' && !session) {
        authLogger.error('Token refresh failed - session is null', {
          event: _event
        });
      }

      if (authLogger.isEnabled()) {
        authLogger.info('Auth state changed', {
          event: _event,
          email: session?.user?.email ?? null
        });
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // On sign-in: send welcome email + process deferred terms acceptance
      if (_event === 'SIGNED_IN' && session?.access_token) {
        // Send welcome email (fire-and-forget, idempotent)
        const welcomeKey = 'wsle:welcomeEmailSent';
        if (!sessionStorage.getItem(welcomeKey)) {
          sessionStorage.setItem(welcomeKey, '1');
          const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
          fetch(`${apiBaseUrl}/api/user/welcome`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
          }).catch(() => {});
        }

        // Process deferred terms acceptance (stored at signup, before email confirmation)
        const pendingTermsVersionId = localStorage.getItem('wsle:pendingTermsAcceptance');
        if (pendingTermsVersionId && session.user) {
          supabase.from('user_terms_acceptance').insert({
            user_id: session.user.id,
            terms_version_id: pendingTermsVersionId,
          }).then(({ error }) => {
            if (!error) {
              localStorage.removeItem('wsle:pendingTermsAcceptance');
            } else {
              authLogger.error('Failed to record deferred terms acceptance', {
                message: error.message,
              });
            }
          });
        }
      }

      // GA4 sign_up for a brand-new Google / Apple account (no-op unless GA4 is on)
      if (_event === 'SIGNED_IN' && session?.user) {
        trackOAuthSignUpIfNew(session.user);
      }

      // Sync RevenueCat identity with Supabase user (native only, no-ops on web)
      if (_event === 'SIGNED_IN' && session?.user?.id) {
        rcIdentifyUser(session.user.id).catch(() => {});
      } else if (_event === 'SIGNED_OUT') {
        logoutRevenueCat().catch(() => {});
      }

      // Dispatch custom event for components that need to react to auth changes
      // Skip TOKEN_REFRESHED — it's a background maintenance event, not a meaningful auth change
      if (typeof window !== 'undefined' && _event !== 'TOKEN_REFRESHED') {
        window.dispatchEvent(new Event('wallstreetle-auth-changed'));
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Sign up with email and password
  // CAPTCHA is verified server-side, then signUp() runs directly in the browser
  // so the PKCE code_verifier is stored in browser localStorage (needed for email confirmation link)
  const signUp = useCallback(async (email: string, password: string, username?: string, captchaToken?: string): Promise<AuthResponse> => {
    setLoading(true);
    try {
      // Step 1: Verify CAPTCHA server-side
      if (captchaToken) {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
        const captchaResponse = await fetch(`${apiBaseUrl}/api/auth/verify-captcha`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ captchaToken }),
        });
        const captchaData = await captchaResponse.json();

        if (!captchaResponse.ok || captchaData.error) {
          return {
            data: { user: null, session: null },
            error: {
              name: 'AuthApiError',
              message: captchaData.error || 'CAPTCHA verification failed',
              status: captchaResponse.status,
            } as any,
          };
        }
      }

      // Step 2: Call supabase.auth.signUp() directly from the browser
      // This ensures the PKCE code_verifier is stored in browser localStorage
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            username: username || email.split('@')[0],
          },
        },
      });

      if (error) {
        return {
          data: { user: null, session: null },
          error,
        } as AuthResponse;
      }

      // Supabase returns a user with no identities when the email is already
      // registered, so only count a real new account (no-op unless GA4 is on).
      if (data.user && (data.user.identities?.length ?? 0) > 0) {
        trackEmailSignUp();
      }

      return {
        data: { user: data.user, session: data.session },
        error: null,
      } as AuthResponse;
    } finally {
      setLoading(false);
    }
  }, []);

  // Sign in with email and password (proxied through backend for server-side CAPTCHA)
  const signIn = useCallback(async (email: string, password: string, captchaToken?: string): Promise<AuthResponse> => {
    setLoading(true);
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/auth/signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, captchaToken }),
      });
      const data = await response.json();

      if (data.error) {
        return {
          data: { user: null, session: null },
          error: {
            name: 'AuthApiError',
            message: data.error,
            status: response.status,
          } as any,
        };
      }

      // Establish session in local Supabase client
      if (data.session) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });

        if (sessionError) {
          return {
            data: { user: null, session: null },
            error: {
              name: 'SessionError',
              message: sessionError.message,
              status: 500,
            } as any,
          };
        }
      }

      // Account status check is handled server-side now
      return {
        data: { user: data.user, session: data.session },
        error: null,
      } as AuthResponse;
    } finally {
      setLoading(false);
    }
  }, []);

  // Sign out
  const signOut = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      // Best-effort: remove this device's push token before the JWT goes away.
      try {
        const { unregisterPushTokenFromBackend } = await import('../services/pushNotifications');
        await unregisterPushTokenFromBackend();
      } catch { /* ignore */ }

      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Sign in with Google
  const signInWithGoogle = useCallback(async (): Promise<void> => {
    // Pre-auth localStorage cleanup to prevent quota errors
    try {
      if (isStorageNearQuota()) {
        const loggerEnabled = authLogger.isEnabled();
        if (loggerEnabled) {
          authLogger.info('Running emergency cleanup before Google sign-in');
        }
        const result = performEmergencyCleanup();
        if (loggerEnabled) {
          authLogger.debug('Emergency cleanup result', {
            provider: 'google',
            freedMB: Number(result.freedMB.toFixed(2)),
            itemsRemoved: result.itemsRemoved,
            success: result.success
          });

          if (!result.success) {
            authLogger.warn('Emergency cleanup did not free enough space', { provider: 'google' });
          }
        }
      }
    } catch (cleanupError) {
      authLogger.warn('Pre-auth cleanup failed', {
        provider: 'google',
        message: cleanupError instanceof Error ? cleanupError.message : 'Unknown error'
      });
      // Continue with sign-in even if cleanup fails
    }

    if (isNativeApp()) {
      // Native: get OAuth URL without navigating, then open in system browser
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: NATIVE_REDIRECT_URI,
          skipBrowserRedirect: true,
        },
      });
      if (error) throw error;
      if (data.url) {
        await loadBrowser();
        await _Browser!.open({ url: data.url });
      }
    } else {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
    }
  }, []);

  // Sign in with Apple
  const signInWithApple = useCallback(async (): Promise<void> => {
    // Pre-auth localStorage cleanup to prevent quota errors
    try {
      if (isStorageNearQuota()) {
        const loggerEnabled = authLogger.isEnabled();
        if (loggerEnabled) {
          authLogger.info('Running emergency cleanup before Apple sign-in');
        }
        const result = performEmergencyCleanup();
        if (loggerEnabled) {
          authLogger.debug('Emergency cleanup result', {
            provider: 'apple',
            freedMB: Number(result.freedMB.toFixed(2)),
            itemsRemoved: result.itemsRemoved,
            success: result.success
          });

          if (!result.success) {
            authLogger.warn('Emergency cleanup did not free enough space', { provider: 'apple' });
          }
        }
      }
    } catch (cleanupError) {
      authLogger.warn('Pre-auth cleanup failed', {
        provider: 'apple',
        message: cleanupError instanceof Error ? cleanupError.message : 'Unknown error'
      });
      // Continue with sign-in even if cleanup fails
    }

    if (isNativeApp()) {
      // Native: get OAuth URL without navigating, then open in system browser
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: NATIVE_REDIRECT_URI,
          skipBrowserRedirect: true,
        },
      });
      if (error) throw error;
      if (data.url) {
        await loadBrowser();
        await _Browser!.open({ url: data.url });
      }
    } else {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
    }
  }, []);

  // Check username availability
  const checkUsernameAvailability = useCallback(async (username: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('check_username_availability', {
        p_username: username
      });

      if (error) {
        console.error('Error checking username availability:', error);
        return false; // Assume unavailable on error (safe default)
      }

      return data; // Function returns true if available
    } catch (error) {
      console.error('Error checking username availability:', error);
      return false; // Assume unavailable on error (safe default)
    }
  }, []);

  // Check email availability
  const checkEmailAvailability = useCallback(async (email: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('check_email_availability', {
        p_email: email
      });

      if (error) {
        console.error('Error checking email availability:', error);
        return false; // Assume unavailable on error (safe default)
      }

      return data; // Function returns true if available
    } catch (error) {
      console.error('Error checking email availability:', error);
      return false; // Assume unavailable on error (safe default)
    }
  }, []);

  // Check email confirmation status (enhanced email check for detecting orphaned accounts)
  const checkEmailConfirmationStatus = useCallback(async (email: string): Promise<{
    exists: boolean;
    confirmed: boolean;
    created_at: string | null;
    available: boolean;
  }> => {
    try {
      const { data, error } = await supabase.rpc('check_email_confirmation_status', {
        p_email: email
      });

      if (error) {
        console.error('Email confirmation check failed:', error);
        // Fallback to simple availability check
        const isAvailable = await checkEmailAvailability(email);
        return {
          exists: !isAvailable,
          confirmed: false,
          created_at: null,
          available: isAvailable
        };
      }

      return data;
    } catch (error) {
      console.error('Email confirmation check failed:', error);
      // Fallback to simple availability check
      const isAvailable = await checkEmailAvailability(email);
      return {
        exists: !isAvailable,
        confirmed: false,
        created_at: null,
        available: isAvailable
      };
    }
  }, [checkEmailAvailability]);

  // Update username
  const updateUsername = useCallback(async (newUsername: string): Promise<{ error: any }> => {
    if (!user) return { error: { message: 'No user logged in' } };

    try {
      const { error } = await supabase.rpc('update_username_across_tables', {
        p_username: newUsername
      });

      if (error) {
        // Handle specific error cases
        if (error.message === 'username_taken') {
          return { error: { message: 'Username already taken' } };
        }
        return { error };
      }

      return { error: null };
    } catch (error) {
      console.error('Error updating username:', error);
      return { error: { message: 'Failed to update username' } };
    }
  }, [user]);

  // Resend verification email
  const resendVerificationEmail = useCallback(async (): Promise<{ error: any }> => {
    if (!user || !user.email) {
      return { error: { message: 'No user email found' } };
    }

    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: user.email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    return { error };
  }, [user]);

  // Check if email is verified
  const isEmailVerified = useMemo(() => {
    return user?.email_confirmed_at != null;
  }, [user]);

  // Accept Terms & Conditions
  const acceptTerms = useCallback(async (termsVersionId: string): Promise<{ error: any }> => {
    if (!user) {
      return { error: { message: 'No user logged in' } };
    }

    try {
      const { error } = await supabase.from('user_terms_acceptance').insert({
        user_id: user.id,
        terms_version_id: termsVersionId,
      });

      if (error) {
        throw new Error(error.message || 'Failed to accept terms');
      }

      return { error: null };
    } catch (err: any) {
      authLogger.error('Error accepting terms', {
        message: err instanceof Error ? err.message : 'Failed to accept terms'
      });
      return { error: { message: err?.message || 'Failed to accept terms' } };
    }
  }, [user]);

  // Request password reset
  const requestPasswordReset = useCallback(async (email: string): Promise<{ error: any }> => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      // For security reasons, we don't reveal if the email exists
      if (error) {
        authLogger.error('Error requesting password reset', {
          message: error.message
        });
      }

      return { error: null };
    } catch (err: any) {
      authLogger.error('Error requesting password reset', {
        message: err instanceof Error ? err.message : 'Unknown error'
      });
      return { error: null }; // Still return success for security
    }
  }, []);

  // Reset password (called after user clicks email link)
  const resetPassword = useCallback(async (newPassword: string): Promise<{ error: any }> => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        authLogger.error('Error resetting password', {
          message: error.message
        });
        return { error: { message: error.message || 'Failed to reset password' } };
      }

      return { error: null };
    } catch (err: any) {
      authLogger.error('Error resetting password', {
        message: err instanceof Error ? err.message : 'Unknown error'
      });
      return { error: { message: err?.message || 'Failed to reset password' } };
    }
  }, []);

  const value: AuthContextType = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    signInWithGoogle,
    signInWithApple,
    updateUsername,
    resendVerificationEmail,
    isEmailVerified,
    checkUsernameAvailability,
    checkEmailAvailability,
    checkEmailConfirmationStatus,
    acceptTerms,
    requestPasswordReset,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
